import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import { ConfirmDialog } from '../../common';
import {
  CloudUpload as UploadIcon,
  Collections as LibraryIcon,
} from '@mui/icons-material';
import { Page } from '../../../../types';
import { useImageUpload } from '../../../../hooks/useImageUpload';
import { ImageLibraryModal } from '../../ImageLibraryModal';
import { ImageMetadata, ImageService } from '../../../../services/imageService';
import { SlideSettings, SlideGrid } from '../../horizontalSlideEditor';

interface HorizontalSlide {
  id: string;
  column: number;
  page?: Page;
  isEmpty: boolean;
}

interface HorizontalSlideTabProps {
  page: Page;
  onSave: (slideData: Partial<Page>) => void;
  siteId?: string;
  editState?: any;
  updateEditState?: (updates: any) => void;
  cachedImages?: ImageMetadata[];
  preloaded?: boolean;
  onChangeSlideData?: (slideData: Partial<Page>) => void;
  /** 新規作成時: スライド画像を一時保存（保存ボタンでアップロード） key=${pageId}-${slideId} */
  onPendingSlideImageFile?: (slideKey: string, file: File | null) => void;
}

export const HorizontalSlideTab: React.FC<HorizontalSlideTabProps> = ({
  page,
  onSave,
  siteId,
  editState,
  updateEditState,
  cachedImages = [],
  preloaded = false,
  onChangeSlideData,
  onPendingSlideImageFile,
}) => {
  const { uploadImage, uploading, uploadProgress } = useImageUpload();
  
  const [horizontalSlides, setHorizontalSlides] = useState<HorizontalSlide[]>([]);
  const [tempColumns, setTempColumns] = useState(1);
  const [showWarning, setShowWarning] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [slideToDelete, setSlideToDelete] = useState<HorizontalSlide | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [selectedSlideId, setSelectedSlideId] = useState<string | null>(null);
  const [imageLibraryModalOpen, setImageLibraryModalOpen] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | undefined>(undefined);
  const [imageMenuAnchor, setImageMenuAnchor] = useState<{ element: HTMLElement; slideId: string } | null>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const [libraryImages, setLibraryImages] = useState<ImageMetadata[]>([]);
  const [libraryVideos, setLibraryVideos] = useState<ImageMetadata[]>([]);
  const [uploadingSlots, setUploadingSlots] = useState<Set<string>>(new Set());
  const [draggedSlide, setDraggedSlide] = useState<HorizontalSlide | null>(null);
  const [slideWidth, setSlideWidth] = useState<number>(100); // スライドの幅（パーセンテージ）
  const [slideWidthInput, setSlideWidthInput] = useState<string>('100'); // スライド幅の入力値（表示用）
  const [viewportWidth, setViewportWidth] = useState<number | undefined>(undefined); // 表示領域の幅（パーセンテージ）
  const [viewportWidthInput, setViewportWidthInput] = useState<string>(''); // 表示領域幅の入力値（表示用）
  const [indicatorPosition, setIndicatorPosition] = useState<'top' | 'bottom'>('bottom'); // ドット位置
  const [infiniteLoop, setInfiniteLoop] = useState<boolean>(true); // 無限ループ設定
  const [initialIndex, setInitialIndex] = useState<number>(1); // 初期表示スライド（1始まり）
  const [positionAdjustment, setPositionAdjustment] = useState<number>(0); // 位置調整（px）
  const [positionAdjustmentInput, setPositionAdjustmentInput] = useState<string>(''); // 位置調整の入力値（表示用）
  const [indicatorOffsetX, setIndicatorOffsetX] = useState<number>(0); // ナビゲーションドット左右位置調整（px）
  const [indicatorOffsetXInput, setIndicatorOffsetXInput] = useState<string>(''); // ドット左右位置調整の入力値（表示用）
  const [indicatorOffsetY, setIndicatorOffsetY] = useState<number>(0); // ナビゲーションドット上下位置調整（px）
  const [indicatorOffsetYInput, setIndicatorOffsetYInput] = useState<string>(''); // ドット上下位置調整の入力値（表示用）
  const initializedRef = useRef(false);

  // 初期化処理
  useEffect(() => {
    if (page && !initializedRef.current) {
      // 既存の横スライドデータがある場合は読み込み
      const existingSlides = page.horizontalSlides || [];
      if (existingSlides.length > 0) {
        setHorizontalSlides(existingSlides.map((slide: any, index: number) => ({
          id: `slide-${index + 1}`,
          column: index + 1,
          page: slide,
          isEmpty: !slide
        })));
        setTempColumns(existingSlides.length);
      } else {
        // デフォルトで1列のスライドを作成
        setHorizontalSlides([{
          id: 'slide-1',
          column: 1,
          page: undefined,
          isEmpty: true
        }]);
        setTempColumns(1);
      }
      // スライド幅の読み込み（デフォルト100%）
      const initialSlideWidth = page.horizontalSlideWidth ?? 100;
      setSlideWidth(initialSlideWidth);
      setSlideWidthInput(String(initialSlideWidth));
      // 表示領域の幅の読み込み
      const initialViewportWidth = page.horizontalSlideViewportWidth;
      setViewportWidth(initialViewportWidth);
      setViewportWidthInput(initialViewportWidth !== undefined ? String(initialViewportWidth) : '');
      // ドット位置の読み込み（デフォルト: bottom）
      setIndicatorPosition(page.horizontalSlideIndicatorPosition || 'bottom');
      // 無限ループ設定の読み込み（デフォルト: true）
      setInfiniteLoop(page.horizontalSlideInfiniteLoop !== false);
      // 初期表示スライド番号（デフォルト: 1）
      setInitialIndex(page.horizontalSlideInitialIndex ?? 1);
      // 位置調整の読み込み（デフォルト: 0）
      const adjustment = page.horizontalSlidePositionAdjustment !== undefined ? page.horizontalSlidePositionAdjustment : 0;
      setPositionAdjustment(adjustment);
      setPositionAdjustmentInput(adjustment === 0 ? '' : String(adjustment));
      // ドット左右位置調整の読み込み（デフォルト: 0）
      const offsetX = page.horizontalSlideIndicatorOffsetX !== undefined ? page.horizontalSlideIndicatorOffsetX : 0;
      setIndicatorOffsetX(offsetX);
      setIndicatorOffsetXInput(offsetX === 0 ? '' : String(offsetX));
      // ドット上下位置調整の読み込み（デフォルト: 0）
      const offsetY = page.horizontalSlideIndicatorOffsetY !== undefined ? page.horizontalSlideIndicatorOffsetY : 0;
      setIndicatorOffsetY(offsetY);
      setIndicatorOffsetYInput(offsetY === 0 ? '' : String(offsetY));
      initializedRef.current = true;
    }
  }, [page]);

  // 列数の変更を適用
  const handleApplyColumns = () => {
    const newColumns = tempColumns;
    
    // 現在のスライド数をチェック
    const currentSlides = horizontalSlides.filter(slide => slide.page && !slide.isEmpty);
    const slidesToRemove = currentSlides.filter(slide => slide.column > newColumns);
    
    if (slidesToRemove.length > 0) {
      setShowWarning(true);
      return;
    }
    
    // 新しいスライド配列を作成
    const newSlides: HorizontalSlide[] = [];
    for (let i = 1; i <= newColumns; i++) {
      const existingSlide = horizontalSlides.find(slide => slide.column === i);
      if (existingSlide) {
        newSlides.push(existingSlide);
      } else {
        newSlides.push({
          id: `slide-${i}`,
          column: i,
          page: undefined,
          isEmpty: true
        });
      }
    }
    
    setHorizontalSlides(newSlides);
    setShowWarning(false);
    setValidationError(null);
  };

  // 列を追加
  const handleAddColumn = () => {
    if (tempColumns < 10) {
      const newColumns = tempColumns + 1;
      setTempColumns(newColumns);
      
      // 新しいスライド配列を作成
      const newSlides = [...horizontalSlides];
      newSlides.push({
        id: `slide-${newColumns}`,
        column: newColumns,
        page: undefined,
        isEmpty: true
      });
      
      setHorizontalSlides(newSlides);
    }
  };

  // 警告を無視して適用
  const handleForceApply = () => {
    const newColumns = tempColumns;
    
    // 範囲外のスライドを削除
    setHorizontalSlides(prevSlides => {
      const filtered = prevSlides.filter(slide => slide.column <= newColumns);
      
      // 不足しているスライドを追加
      const newSlides = [...filtered];
      for (let i = filtered.length + 1; i <= newColumns; i++) {
        newSlides.push({
          id: `slide-${i}`,
          column: i,
          page: undefined,
          isEmpty: true
        });
      }
      
      return newSlides;
    });
    
    setShowWarning(false);
  };

  // ライブラリ画像を読み込む
  useEffect(() => {
    const loadLibraryImages = async () => {
      if (!siteId || siteId === 'create') return;
      try {
        if (preloaded && cachedImages.length > 0) {
          const images = cachedImages.filter(img => !img.isVideo);
          const videos = cachedImages.filter(img => img.isVideo);
          setLibraryImages(images);
          setLibraryVideos(videos);
        } else {
          const allMedia = await ImageService.getSiteImageLibrary(siteId);
          const images = allMedia.filter(img => !img.isVideo);
          const videos = allMedia.filter(img => img.isVideo);
          setLibraryImages(images);
          setLibraryVideos(videos);
        }
      } catch (error) {
        console.error('画像の読み込みに失敗しました:', error);
      }
    };

    if (siteId) {
      loadLibraryImages();
    }
  }, [siteId, cachedImages, preloaded]);

  // 使用中の画像URLを集約
  const getUsedImageUrls = (): Set<string> => {
    const usedUrls = new Set<string>();
    
    if (!editState) return usedUrls;
    
    // ページの背景画像
    if (editState.pages && Array.isArray(editState.pages)) {
      editState.pages.forEach((page: any) => {
        if (page.backgroundImage) {
          usedUrls.add(page.backgroundImage);
        }
        // 横スライドの画像
        if (page.horizontalSlides && Array.isArray(page.horizontalSlides)) {
          page.horizontalSlides.forEach((slide: any) => {
            if (slide.backgroundImage) {
              usedUrls.add(slide.backgroundImage);
            }
          });
        }
      });
    }
    
    // サイト設定の背景画像
    if (editState.settings?.backgroundImage) {
      usedUrls.add(editState.settings.backgroundImage);
    }
    
    return usedUrls;
  };

  // 画像選択処理（Menuを表示）
  const handleImageSelect = (slideId: string, event: React.MouseEvent<HTMLElement>) => {
    if (!siteId) return;
    const slide = horizontalSlides.find(s => s.id === slideId);
    setSelectedSlideId(slideId);
    setCurrentImageUrl(slide?.page?.backgroundImage);
    setImageMenuAnchor({ element: event.currentTarget, slideId });
  };

  // デバイスから画像をアップロード（新規時は一時保存のみ）
  const handleImageFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedSlideId) return;
    if (!ImageService.isSupportedImageType(file) || !ImageService.isValidFileSize(file, 100)) {
      alert('画像ファイル（JPEG/PNG/WebP/GIF、最大100MB）を選択してください');
      return;
    }
    if (siteId === 'create' && page?.id && onPendingSlideImageFile) {
      onPendingSlideImageFile(`${page.id}-${selectedSlideId}`, file);
      processImageFileCreateMode(file, selectedSlideId);
    } else if (siteId && siteId !== 'create') {
      processImageFile(file, selectedSlideId);
    }
    if (imageFileInputRef.current) imageFileInputRef.current.value = '';
  };

  // 画像が選択された時の処理（ライブラリから）
  const handleImageSelected = (imageUrl: string) => {
    if (selectedSlideId) {
      setHorizontalSlides(prevSlides => {
        const updatedSlides = prevSlides.map(slide => {
          if (slide.id === selectedSlideId) {
            return {
              ...slide,
              page: buildSlidePageWithUpdatedImage(slide, imageUrl),
              isEmpty: false
            };
          }
          return slide;
        });
        
        return updatedSlides;
      });
    }
    setImageLibraryModalOpen(false);
    setImageMenuAnchor(null);
    setSelectedSlideId(null);
  };

  // スライド削除モーダルを開く
  const handleDeleteSlide = (slide: HorizontalSlide) => {
    if (!slide.page) return;
    setSlideToDelete(slide);
    setShowDeleteDialog(true);
  };

  // スライド削除を確定
  const confirmDeleteSlide = () => {
    if (!slideToDelete) return;
    if (siteId === 'create' && page?.id) onPendingSlideImageFile?.(`${page.id}-${slideToDelete.id}`, null);
    setHorizontalSlides(prevSlides => prevSlides.map(s =>
      s.id === slideToDelete.id ? { ...s, page: undefined, isEmpty: true } : s
    ));
    setShowDeleteDialog(false);
    setSlideToDelete(null);
  };

  const buildSlidePageWithUpdatedImage = (slide: HorizontalSlide, imageUrl: string) => ({
    ...(slide.page ?? {}),
    id: slide.page?.id || `horizontal-slide-${slide.column}`,
    pageId: slide.page?.pageId || `${page.pageId}-h${slide.column}`,
    row: page.row,
    column: slide.column.toString(),
    type: 'horizontal' as const,
    title: (slide.page as any)?.title || `横スライド ${slide.column}`,
    isHorizontal: true,
    parentPageId: slide.page?.parentPageId || page.id,
    backgroundImage: imageUrl
  });

  // 新規作成時のみ: プレビュー表示（アップロードは保存時に実行）
  const processImageFileCreateMode = (file: File, slideId: string) => {
    if (!file.type.startsWith('image/') || file.size > 100 * 1024 * 1024) return;
    const localImageUrl = URL.createObjectURL(file);
    setHorizontalSlides(prevSlides => prevSlides.map(slide => {
      if (slide.id !== slideId) return slide;
      return {
        ...slide,
        page: buildSlidePageWithUpdatedImage(slide, localImageUrl),
        isEmpty: false
      };
    }));
  };

  // ファイル処理
  const processImageFile = (file: File, slideId: string) => {
    if (siteId === 'create' && page?.id && onPendingSlideImageFile) {
      onPendingSlideImageFile(`${page.id}-${slideId}`, file);
      processImageFileCreateMode(file, slideId);
      return;
    }
    if (!siteId) {
      alert('サイトIDが見つかりません。ページをリロードしてください。');
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください。');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      alert('ファイルサイズが大きすぎます。100MB以下のファイルを選択してください。');
      return;
    }
    const localImageUrl = URL.createObjectURL(file);
    setHorizontalSlides(prevSlides => {
      const updatedSlides = prevSlides.map(slide => {
        if (slide.id === slideId) {
          return {
            ...slide,
            page: buildSlidePageWithUpdatedImage(slide, localImageUrl),
            isEmpty: false
          };
        }
        return slide;
      });
      
      return updatedSlides;
    });

    setUploadingSlots(prev => new Set(prev).add(slideId));

    uploadImage(file, siteId, slideId).then((firebaseUrl: string) => {
      const img = new Image();
      img.onload = () => {
        setHorizontalSlides(prevSlides => {
          const updatedSlides = prevSlides.map(slide => 
            slide.id === slideId && slide.page
              ? { 
                  ...slide, 
                  page: { 
                    ...slide.page, 
                    backgroundImage: firebaseUrl 
                  } 
                }
              : slide
          );
          
          return updatedSlides;
        });
        
        setUploadingSlots(prev => {
          const newSet = new Set(prev);
          newSet.delete(slideId);
          return newSet;
        });
        
        URL.revokeObjectURL(localImageUrl);
      };
      img.src = firebaseUrl;
      
    }).catch((error: any) => {
      console.error('画像アップロードに失敗しました:', error);
      
      setUploadingSlots(prev => {
        const newSet = new Set(prev);
        newSet.delete(slideId);
        return newSet;
      });
      
      alert('画像のアップロードに失敗しました。再試行してください。');
    });
  };

  // ドラッグ&ドロップ処理
  const handleDragStart = (e: React.DragEvent, slide: HorizontalSlide) => {
    if (!slide.page) return;
    setDraggedSlide(slide);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetSlide: HorizontalSlide) => {
    e.preventDefault();
    
    if (!draggedSlide || draggedSlide.id === targetSlide.id) {
      setDraggedSlide(null);
      return;
    }

    setHorizontalSlides(prevSlides => {
      const newSlides = [...prevSlides];
      const draggedIndex = newSlides.findIndex(s => s.id === draggedSlide.id);
      const targetIndex = newSlides.findIndex(s => s.id === targetSlide.id);

      if (draggedIndex === -1 || targetIndex === -1) return prevSlides;

      const draggedPage = newSlides[draggedIndex].page;
      const targetPage = newSlides[targetIndex].page;

      // 入れ替え処理
      if (draggedPage && targetPage) {
        const updatedDraggedPage = {
          ...draggedPage,
          column: targetSlide.column.toString(),
          pageId: `${page.pageId}-h${targetSlide.column}`
        };

        const updatedTargetPage = {
          ...targetPage,
          column: draggedSlide.column.toString(),
          pageId: `${page.pageId}-h${draggedSlide.column}`
        };

        newSlides[draggedIndex] = {
          ...newSlides[draggedIndex],
          page: updatedTargetPage,
          isEmpty: false
        };

        newSlides[targetIndex] = {
          ...newSlides[targetIndex],
          page: updatedDraggedPage,
          isEmpty: false
        };
      } else if (draggedPage && !targetPage) {
        const updatedDraggedPage = {
          ...draggedPage,
          column: targetSlide.column.toString(),
          pageId: `${page.pageId}-h${targetSlide.column}`
        };

        newSlides[draggedIndex] = {
          ...newSlides[draggedIndex],
          page: undefined,
          isEmpty: true
        };

        newSlides[targetIndex] = {
          ...newSlides[targetIndex],
          page: updatedDraggedPage,
          isEmpty: false
        };
      }

      return newSlides;
    });

    setDraggedSlide(null);
  };

  const handleDragEnd = () => {
    setDraggedSlide(null);
  };

  // ドロップ処理（統一）
  const handleDropUnified = (e: React.DragEvent, slide: HorizontalSlide) => {
    e.preventDefault();
    
    // ファイルがドロップされた場合
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      const imageFile = files.find(file => file.type.startsWith('image/'));
      
      if (imageFile) {
        processImageFile(imageFile, slide.id);
        return;
      }
    }
    
    // スライドがドラッグされた場合
    if (draggedSlide && draggedSlide.page) {
      handleDrop(e, slide);
    }
  };

  // 親（ダイアログ）へ現在の横スライド設定を一時通知（適用時にまとめて保存される）
  const prevSlideDataRef = useRef<Partial<Page> | null>(null);
  useEffect(() => {
    if (!initializedRef.current) return;
    if (!onChangeSlideData) return;
    
    const slideData: Partial<Page> = {
      horizontalSlides: horizontalSlides
        .map(slide => slide.page)
        .filter((p): p is Page => p !== undefined),
      horizontalSlideWidth: slideWidth,
      horizontalSlideIndicatorPosition: indicatorPosition,
      horizontalSlideInfiniteLoop: infiniteLoop,
      horizontalSlideInitialIndex: initialIndex,
      horizontalSlidePositionAdjustment: positionAdjustment,
      horizontalSlideIndicatorOffsetX: indicatorOffsetX,
      horizontalSlideIndicatorOffsetY: indicatorOffsetY,
    };
    // viewportWidthがundefinedでない場合のみ追加（Firestoreはundefinedをサポートしていない）
    if (viewportWidth !== undefined) {
      slideData.horizontalSlideViewportWidth = viewportWidth;
    }
    
    // 前回の値と比較して、変更があった場合のみ呼び出す
    const prevData = prevSlideDataRef.current;
    if (prevData && 
        JSON.stringify(prevData.horizontalSlides) === JSON.stringify(slideData.horizontalSlides) &&
        prevData.horizontalSlideWidth === slideData.horizontalSlideWidth &&
        prevData.horizontalSlideViewportWidth === slideData.horizontalSlideViewportWidth &&
        prevData.horizontalSlideIndicatorPosition === slideData.horizontalSlideIndicatorPosition &&
        prevData.horizontalSlideInfiniteLoop === slideData.horizontalSlideInfiniteLoop &&
        prevData.horizontalSlideInitialIndex === slideData.horizontalSlideInitialIndex &&
        prevData.horizontalSlidePositionAdjustment === slideData.horizontalSlidePositionAdjustment &&
        prevData.horizontalSlideIndicatorOffsetX === slideData.horizontalSlideIndicatorOffsetX &&
        prevData.horizontalSlideIndicatorOffsetY === slideData.horizontalSlideIndicatorOffsetY) {
      return;
    }
    
    prevSlideDataRef.current = slideData;
    onChangeSlideData(slideData);
  }, [horizontalSlides, slideWidth, viewportWidth, indicatorPosition, infiniteLoop, initialIndex, positionAdjustment, indicatorOffsetX, indicatorOffsetY, onChangeSlideData]);

  return (
    <Box sx={{ pt: 0, px: 0, pb: 0, height: 'auto' }}>
      {/* 画像選択ダイアログ ※保存済みサイトのみ */}
      {siteId && siteId !== 'create' && (
        <ImageLibraryModal
          open={imageLibraryModalOpen}
          onClose={() => { setImageLibraryModalOpen(false); setImageMenuAnchor(null); }}
          onSelect={handleImageSelected}
          images={libraryImages}
          videos={libraryVideos}
          title="横スライド画像を選択"
          usedImageUrls={getUsedImageUrls()}
        />
      )}
      {/* 画像選択メニュー（新規時も表示） */}
      {siteId && (
        <>
          <Menu
            anchorEl={imageMenuAnchor?.element || null}
            open={Boolean(imageMenuAnchor)}
            onClose={() => setImageMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.15)', mt: 0.5, minWidth: 180 } }}
          >
            <MenuItem onClick={() => { setImageMenuAnchor(null); imageFileInputRef.current?.click(); }} sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}><UploadIcon sx={{ fontSize: '18px', color: '#616161' }} /></Box>
              デバイスから選択
            </MenuItem>
            <MenuItem onClick={() => { setImageMenuAnchor(null); if (siteId !== 'create') setImageLibraryModalOpen(true); }} sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}><LibraryIcon sx={{ fontSize: '18px', color: '#616161' }} /></Box>
              ライブラリから選択
            </MenuItem>
          </Menu>
          <input ref={imageFileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/gif" onChange={handleImageFileSelect} style={{ display: 'none' }} />
        </>
      )}

      {/* スライド設定 */}
      <SlideSettings
        tempColumns={tempColumns}
        slideWidth={slideWidth}
        slideWidthInput={slideWidthInput}
        viewportWidth={viewportWidth}
        viewportWidthInput={viewportWidthInput}
        positionAdjustment={positionAdjustment}
        positionAdjustmentInput={positionAdjustmentInput}
        initialIndex={initialIndex}
        indicatorPosition={indicatorPosition}
        infiniteLoop={infiniteLoop}
        horizontalSlidesLength={horizontalSlides.length}
        indicatorOffsetX={indicatorOffsetX}
        indicatorOffsetXInput={indicatorOffsetXInput}
        indicatorOffsetY={indicatorOffsetY}
        indicatorOffsetYInput={indicatorOffsetYInput}
        onTempColumnsChange={setTempColumns}
        onApplyColumns={handleApplyColumns}
        onSlideWidthChange={setSlideWidth}
        onSlideWidthInputChange={setSlideWidthInput}
        onSlideWidthBlur={(value) => {
          if (value === '') {
            setSlideWidth(0);
            setSlideWidthInput('');
            return;
          }
          const numValue = parseInt(value);
          if (isNaN(numValue) || numValue < 0) {
            setSlideWidth(0);
            setSlideWidthInput('0');
          } else if (numValue > 100) {
            setSlideWidth(100);
            setSlideWidthInput('100');
          } else {
            setSlideWidth(numValue);
            setSlideWidthInput(String(numValue));
          }
        }}
        onViewportWidthChange={setViewportWidth}
        onViewportWidthInputChange={setViewportWidthInput}
        onViewportWidthBlur={(value) => {
          if (value === '') {
            setViewportWidth(0);
            setViewportWidthInput('');
            return;
          }
          const numValue = parseInt(value);
          if (isNaN(numValue) || numValue < 0) {
            setViewportWidth(0);
            setViewportWidthInput('0');
          } else if (numValue > 100) {
            setViewportWidth(100);
            setViewportWidthInput('100');
          } else {
            setViewportWidth(numValue);
            setViewportWidthInput(String(numValue));
          }
        }}
        onPositionAdjustmentChange={setPositionAdjustment}
        onPositionAdjustmentInputChange={setPositionAdjustmentInput}
        onPositionAdjustmentBlur={() => {
          if (positionAdjustmentInput === '' || positionAdjustmentInput === '-' || positionAdjustmentInput === '+') {
            setPositionAdjustmentInput('');
          }
        }}
        onInitialIndexChange={setInitialIndex}
        onIndicatorPositionChange={setIndicatorPosition}
        onInfiniteLoopChange={setInfiniteLoop}
        onIndicatorOffsetXChange={setIndicatorOffsetX}
        onIndicatorOffsetXInputChange={setIndicatorOffsetXInput}
        onIndicatorOffsetXBlur={() => {
          if (indicatorOffsetXInput === '' || indicatorOffsetXInput === '-' || indicatorOffsetXInput === '+') {
            setIndicatorOffsetXInput('');
          }
        }}
        onIndicatorOffsetYChange={setIndicatorOffsetY}
        onIndicatorOffsetYInputChange={setIndicatorOffsetYInput}
        onIndicatorOffsetYBlur={() => {
          if (indicatorOffsetYInput === '' || indicatorOffsetYInput === '-' || indicatorOffsetYInput === '+') {
            setIndicatorOffsetYInput('');
          }
        }}
      />

      {/* バリデーションエラー表示 */}
      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      {/* スライドグリッド */}
      <SlideGrid
        horizontalSlides={horizontalSlides}
        uploadingSlots={uploadingSlots}
        draggedSlide={draggedSlide}
        onDragStart={handleDragStart}
        onDrop={handleDropUnified}
        onDragEnd={handleDragEnd}
        onImageSelect={handleImageSelect}
        onDeleteSlide={handleDeleteSlide}
        onProcessImageFile={processImageFile}
        onAddColumn={handleAddColumn}
        maxColumns={tempColumns}
      />



      {/* 警告ダイアログ */}
      <Dialog open={showWarning} onClose={() => setShowWarning(false)}>
        <DialogTitle>警告</DialogTitle>
        <DialogContent>
          <Typography>
            列数を減らすと、既存のスライドが削除されます。
            続行しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWarning(false)}>キャンセル</Button>
          <Button onClick={handleForceApply} color="error" variant="contained">
            削除して適用
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSlideToDelete(null);
        }}
        onConfirm={confirmDeleteSlide}
        message="削除します。よろしいですか？"
        confirmText="削除"
        confirmColor="#DB2323"
        confirmHoverColor="#C01F1F"
      />
    </Box>
  );
};
